import styled from 'styled-components';

import { Box } from 'components/Box';
import { Divider } from 'components/Divider';
import {
  CheckboxField,
  CreditCardSecureNotice,
  CurrencyInputField,
  Form,
  FormSubmitFunction,
  GeneralError,
  HiddenField,
  InputField,
  RadioButtonsField,
  RecaptchaField,
  SubmitButton,
} from 'components/Form';
import { AddressWithoutLocationForm } from 'components/Form/AddressWithoutLocationForm';
import { CreditCardFieldWithDefaultAdditionalData } from 'components/Form/CreditCard/CreditCardFieldWithDefaultAdditionalData';
import { Icon } from 'components/Icon';
import { H3 } from 'components/Text/H3';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { gutterWidth, transition } from 'theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import { cmsGetDonateBlockSubmitButtonText } from './cmsGetDonateBlockSubmitButtonText';

const AMOUNTS = [
  {
    label: '$25',
    value: 25,
  },
  {
    label: '$50',
    value: 50,
  },
  {
    label: '$100',
    value: 100,
  },
  {
    label: getText('Other Amount'),
    value: 'OTHER',
  },
];
const StyledSubmitButton = styled(SubmitButton)`
  svg {
    transition: transform ${transition};
  }
  &:hover,
  &:focus {
    svg {
      transform: translateX(50%);
    }
  }
`;

type DonateBlockFormValues = {
  amount?: string;
  otherAmount?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  country: string | null;
  isForHonoree?: boolean;
};

type Props = {
  onSubmit: FormSubmitFunction<DonateBlockFormValues>;
  campaignId: string | null;
  initialValues: Record<string, unknown>;
};

export function CmsDonateBlockInternalForm({
  onSubmit,
  campaignId,
  initialValues,
}: Props) {
  const { user } = useUserEnvironment();
  const loggedIn = Boolean(user);

  return (
    <Form
      analyticsTitle="[cms] Donate block internal block"
      usageContext="page"
      data-qa-id="donate-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ getValue }) => {
        const amount = getValue('amount') as string;

        return (
          <>
            <GeneralError />
            <H3>{getText('Your Donation')}</H3>
            <HiddenField
              name="campaignId"
              // @ts-expect-error TS(2322): Type '{ name: string; value: string; }' is not ass... Remove this comment to see the full error message
              value={campaignId}
            />
            <RadioButtonsField
              buttons={AMOUNTS}
              name="amount"
              label={getText('How much would you like to donate? (USD)')}
              onUpdateValue={(v) =>
                trackClicked('Preset Donation Amount', {
                  amount: v,
                  campaign_id: campaignId,
                })
              }
              square
            />
            {amount === 'OTHER' ? (
              <CurrencyInputField
                name="otherAmount"
                min={5}
                errorMessage={getText('Please enter an amount of $5 or more')}
              />
            ) : null}

            <Box my={[gutterWidth / 2, null, gutterWidth]}>
              <Divider light dashed marginTop={0} marginBottom={0} />
            </Box>

            <H3>{getText('Payment Info')}</H3>

            <div style={{ marginBottom: 22 }}>
              <CreditCardSecureNotice />
            </div>

            <CreditCardFieldWithDefaultAdditionalData wrap />

            <Box my={[gutterWidth / 2, null, gutterWidth]}>
              <Divider light dashed marginTop={0} marginBottom={0} />
            </Box>

            <H3>{getText('Billing Info')}</H3>
            <InputField
              name="firstName"
              label={getText('First Name')}
              placeholder={getText('Casey')}
            />
            <InputField
              name="lastName"
              label={getText('Last Name')}
              placeholder={getText('Smith')}
            />
            <AddressWithoutLocationForm
              useUSStateDropdown={getValue('country') === 'US'}
            />

            <Box
              mb={[gutterWidth / 2, null, gutterWidth]}
              mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
            >
              <Divider light dashed marginTop={0} marginBottom={0} />
            </Box>

            <H3>{getText('Contact Info')}</H3>
            <InputField
              type="email"
              name="email"
              placeholder={getText('casey.smith@example.com')}
              label={getText('Email')}
            />
            <InputField
              name="phone"
              placeholder={getText('e.g. 123 555-1234')}
              label={getText('Phone Number (optional)')}
            />

            <Box
              mb={[gutterWidth / 2, null, gutterWidth]}
              mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
            >
              <Divider light dashed marginTop={0} marginBottom={0} />
            </Box>

            <CheckboxField
              name="isForHonoree"
              label={getText(
                'Dedicate this gift in honor or in memory of someone',
              )}
            />
            {getValue('isForHonoree') ? (
              <>
                <H3>{getText('Who is this gift dedicated to?')}</H3>
                <InputField
                  name="honoreeFirstName"
                  label={`${getText(`First Name`)}*`}
                  placeholder={getText('Casey')}
                />
                <InputField
                  name="honoreeLastName"
                  label={`${getText(`Last Name`)}*`}
                  placeholder={getText('Smith')}
                />
                <InputField
                  name="honoreeEmail"
                  type="email"
                  label={getText('Email for E-card (optional)')}
                  description={getText(
                    'An e-card will be sent to the email address provided',
                  )}
                  placeholder={getText('casey.smith@example.com')}
                />

                <Box
                  mb={[gutterWidth / 2, null, gutterWidth]}
                  mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
                >
                  <Divider light dashed marginTop={0} marginBottom={0} />
                </Box>
              </>
            ) : null}

            {loggedIn ? null : <RecaptchaField />}

            <Box
              mt={[gutterWidth / 2, null, gutterWidth]}
              display="flex"
              justifyContent="flex-end"
            >
              <StyledSubmitButton size="large">
                {cmsGetDonateBlockSubmitButtonText(
                  amount === 'OTHER'
                    ? (getValue('otherAmount') as string)
                    : amount,
                )}
                <Box display="inline-block" ml="4px">
                  <Icon initialColorName="white" name="arrow-right" size={18} />
                </Box>
              </StyledSubmitButton>
            </Box>
          </>
        );
      }}
    />
  );
}
